/**
 * Slug rewriting for portal and www routes.
 *
 * All route metadata is defined in co-located .meta.ts files next to each route.
 *
 * Used by:
 * - Router rewrites (input: external slug → internal route, output: internal route → locale slug)
 * - Language switcher (translate current slug to target locale)
 * - SEO components (canonical URL and hreflang alternates)
 * - Sitemaps (iterate routes to generate URLs with metadata)
 */
// --- Portal meta files (co-located with each route) -----------------------
import * as portalPrivacyPolicy from '@/routes/$portalId/privacy-policy.meta';
import * as portalLegalDisclaimer from '@/routes/$portalId/legal-disclaimer.meta';

// --- Redesign meta files, now the live site (co-located with each route) ---
import * as wwwIndex from '@/routes/www/index.meta';
import * as wwwSolutions from '@/routes/www/solutions.meta';
import * as wwwPartnerNgos from '@/routes/www/partner-ngos.meta';
import * as wwwAboutUs from '@/routes/www/about-us.meta';
import * as wwwContact from '@/routes/www/contact.meta';
import * as wwwFaq from '@/routes/www/faq.meta';
import * as wwwLegalDisclaimer from '@/routes/www/legal-disclaimer.meta';
import * as wwwPrivacyPolicy from '@/routes/www/privacy-policy.meta';
import * as wwwCookies from '@/routes/www/cookies.meta';
import * as wwwSelectionCriteria from '@/routes/www/selection-criteria.meta';
import * as wwwLiveCounter from '@/routes/www/live-counter.meta';

// --- Archived site meta files (`old.worldcoo.com`) -------------------------
import * as oldIndex from '@/routes/old/index.meta';
import * as oldAboutUs from '@/routes/old/about-us.meta';
import * as oldContact from '@/routes/old/contact.meta';
import * as oldRoundUp from '@/routes/old/round-up.meta';
import * as oldTransparency from '@/routes/old/transparency.meta';
import * as oldPrivacyPolicy from '@/routes/old/privacy-policy.meta';
import * as oldLegalDisclaimer from '@/routes/old/legal-disclaimer.meta';
import * as oldCookies from '@/routes/old/cookies.meta';
import * as oldSelectionCriteria from '@/routes/old/selection-criteria.meta';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * One path segment below a page: its word per locale, and whatever it owns in turn.
 *
 * Recursive because the contact page is two deep — `/contacto/ong/consulta` — and a second bespoke
 * map for the third segment is how the three consumers (rewrite, language switcher, canonical) start
 * disagreeing about which part of a URL they own.
 */
export interface SlugNode {
	slugs: Record<string, string>;
	children?: Record<string, SlugNode>;
}

export interface RouteSlugEntry {
	routeName: string;
	slugs: Record<string, string>;
	/** Sitemap metadata — pages without this field are excluded from the sitemap */
	sitemap?: { changefreq: string; priority: string; lastmod: string };
	/**
	 * The tree of path segments this route owns, as `internal name → node`.
	 *
	 * Only a route that declares them is rewritten at a depth greater than one segment, and only a
	 * segment the tree actually names is translated. That gate is the point: `slugToRoute` is consulted
	 * for `segments[1]` whatever the depth, and a portal serves `/{portal}/{campaign}/...` under the
	 * same rewrite — so relaxing the depth for every route would let a campaign whose slug happened to
	 * match a legal page's be rewritten out from under it. Declaring the tree, rather than a depth,
	 * keeps that impossible however deep a page grows.
	 */
	children?: Record<string, SlugNode>;
}

export interface SlugMaps {
	/** External slug (any locale) → internal route segment */
	slugToRoute: Record<string, string>;
	/** Internal route segment → { locale: slug } */
	routeToSlugs: Record<string, Record<string, string>>;
	/** Internal route segment → the tree of segments below it */
	routeChildren: Record<string, Record<string, SlugNode>>;
}

// ---------------------------------------------------------------------------
// Map builder
// ---------------------------------------------------------------------------

function buildMaps(routes: RouteSlugEntry[]): SlugMaps {
	const slugToRoute: Record<string, string> = {};
	const routeToSlugs: Record<string, Record<string, string>> = {};
	const routeChildren: Record<string, Record<string, SlugNode>> = {};

	for (const { routeName, slugs, children } of routes) {
		routeToSlugs[routeName] = slugs;
		for (const locale in slugs) {
			if (!Object.hasOwn(slugs, locale)) continue;
			slugToRoute[slugs[locale]!] = routeName;
		}
		if (children) routeChildren[routeName] = children;
	}

	return { slugToRoute, routeToSlugs, routeChildren };
}

/** The internal name a segment carries in any locale, or undefined when the tree does not name it. */
function nameOfSlug(nodes: Record<string, SlugNode>, slug: string): string | undefined {
	for (const name in nodes) {
		if (!Object.hasOwn(nodes, name)) continue;
		const slugs = nodes[name]!.slugs;
		for (const locale in slugs) {
			if (Object.hasOwn(slugs, locale) && slugs[locale] === slug) return name;
		}
	}
	return undefined;
}

/**
 * Translate the segments BELOW a page, in either direction, walking the tree it declares.
 *
 * It stops at the first segment the tree does not name and leaves that one and everything after it
 * alone, so an invented segment reaches the router untouched and 404s there rather than being
 * silently rewritten into something that exists.
 */
function walkSegments(
	nodes: Record<string, SlugNode> | undefined,
	segments: string[],
	translate: (node: SlugNode, name: string) => string | undefined,
): string[] {
	const out: string[] = [];
	let level = nodes;
	for (const segment of segments) {
		const name = level ? (level[segment] ? segment : nameOfSlug(level, segment)) : undefined;
		const node = name && level ? level[name] : undefined;
		if (!name || !node) return [...out, ...segments.slice(out.length)];
		out.push(translate(node, name) ?? segment);
		level = node.children;
	}
	return out;
}

/** External (any locale) → internal names, for the segments below a page. */
export function toInternalSegments(routeName: string, segments: string[], context: SlugContext): string[] {
	return walkSegments(SLUG_CONTEXTS[context].routeChildren[routeName], segments, (_node, name) => name);
}

/** Internal names → the words `locale` puts in the URL, for the segments below a page. */
export function toLocaleSegments(
	routeName: string,
	segments: string[],
	locale: string,
	context: SlugContext,
): string[] {
	return walkSegments(SLUG_CONTEXTS[context].routeChildren[routeName], segments, (node) => node.slugs[locale]);
}

/** Whether a route owns any segments below it — the gate the rewrite reads before looking deeper. */
export function hasChildSegments(routeName: string, context: SlugContext): boolean {
	return SLUG_CONTEXTS[context].routeChildren[routeName] !== undefined;
}

// ---------------------------------------------------------------------------
// Portal routes — add new entries here when creating a portal page with slugs
// ---------------------------------------------------------------------------

export const portalRoutes: RouteSlugEntry[] = [portalPrivacyPolicy, portalLegalDisclaimer];
export const portalSlugs = buildMaps(portalRoutes);

// ---------------------------------------------------------------------------
// Archived site routes (`old.worldcoo.com`) — the site the redesign replaced.
//
// Kept reachable, and kept out of the index: it shares almost every slug with the live site above,
// so an indexed copy competes with the real page as duplicate content. See `old/robots[.]txt.tsx`
// and `old/-components/page-seo.tsx`. Nothing new belongs here.
// ---------------------------------------------------------------------------

export const oldRoutes: RouteSlugEntry[] = [
	oldIndex,
	oldAboutUs,
	oldContact,
	oldRoundUp,
	oldTransparency,
	oldPrivacyPolicy,
	oldLegalDisclaimer,
	oldCookies,
	oldSelectionCriteria,
];

export const oldSlugs = buildMaps(oldRoutes);

// ---------------------------------------------------------------------------
// WWW routes — add new entries here when creating a www page with slugs
// ---------------------------------------------------------------------------

export const wwwRoutes: RouteSlugEntry[] = [
	wwwIndex,
	wwwSolutions,
	wwwPartnerNgos,
	wwwAboutUs,
	wwwContact,
	wwwFaq,
	wwwLegalDisclaimer,
	wwwPrivacyPolicy,
	wwwCookies,
	wwwSelectionCriteria,
	wwwLiveCounter,
];
export const wwwSlugs = buildMaps(wwwRoutes);

// ---------------------------------------------------------------------------
// Contexts
// ---------------------------------------------------------------------------

/**
 * Which set of slugs a URL belongs to. It is decided by the HOST — the router's rewrite reads the
 * subdomain and never consults another context's map — so every consumer has to name the same one, or
 * it answers with a slug the host does not serve.
 */
export const SLUG_CONTEXTS = {
	www: wwwSlugs,
	old: oldSlugs,
	portal: portalSlugs,
} as const;

export type SlugContext = keyof typeof SLUG_CONTEXTS;

// ---------------------------------------------------------------------------
// Language switcher helper
// ---------------------------------------------------------------------------

/** Reverse map, PER CONTEXT: slug → its locale slugs. */
const slugToSlugsByContext = Object.fromEntries(
	Object.entries(SLUG_CONTEXTS).map(([context, maps]) => {
		const bySlug: Record<string, Record<string, string>> = {};
		for (const route in maps.routeToSlugs) {
			if (!Object.hasOwn(maps.routeToSlugs, route)) continue;
			const slugsByLocale = maps.routeToSlugs[route]!;
			for (const locale in slugsByLocale) {
				if (!Object.hasOwn(slugsByLocale, locale)) continue;
				bySlug[slugsByLocale[locale]!] = slugsByLocale;
			}
		}
		return [context, bySlug];
	}),
) as Record<SlugContext, Record<string, Record<string, string>>>;

/**
 * Translate the segments BELOW a page slug into another locale, in one step.
 *
 * The language switcher's entry point: it holds the URL the visitor is on, so the segments arrive as
 * whatever locale that URL is written in, and the walk accepts any of them.
 */
export function translateChildSegments(
	slug: string,
	segments: string[],
	targetLocale: string,
	context: SlugContext,
): string[] {
	const routeName = SLUG_CONTEXTS[context].slugToRoute[slug];
	if (!routeName) return segments;
	return toLocaleSegments(routeName, toInternalSegments(routeName, segments, context), targetLocale, context);
}

/**
 * Translate a page slug to its equivalent in another locale, WITHIN one context.
 *
 * The context argument is not optional politeness — it is the fix for a real bug. This used to read a
 * single map merged across every context, and merging is lossy: `about-us` is both www's own `en`
 * slug and the archived site's `en` slug for `sobre-nosotros`, so switching to Catalan on www's
 * `/es/sobre-nosotros` could answer with a path only the other host serves. Whoever registered last
 * won. Ask for the context you are on and that cannot happen.
 *
 * Returns undefined when the slug is not one this context knows.
 */
export function translateSlug(slug: string, targetLocale: string, context: SlugContext): string | undefined {
	return slugToSlugsByContext[context][slug]?.[targetLocale];
}
